// utils/tadbeesa.js
//
// Pure helpers for «تدبيسة النادي» — the club's own resale shelf. No return
// door, and (P2-A) a per-size cap instead of the flat post-level one; both
// read off the SAME fact, so it lives in one place: is this line/post from
// the تدبيسة channel? See composables/useTadbeesaChannel.js for how that
// channel's id gets resolved, and the 2026-09-22 backend brief for the
// domain contract this mirrors.

/**
 * Is this order-box line from the «تدبيسة النادي» channel? Compared as
 * strings — `item.channelId` and the resolved channel id can each arrive as
 * either a string or a number depending on the path that produced them, and
 * a strict `===` between the two types would silently read as "no" forever.
 * False whenever either side is null/undefined: an unresolved تدبيسة channel
 * (lookup still in flight, or failed) must never make an ordinary line look
 * like one of its own — the safe direction, since this only ever HIDES a
 * button or narrows a cap, and the backend enforces the real rule regardless.
 *
 * @param {{channelId: string|number|null|undefined}|null|undefined} item
 * @param {string|number|null|undefined} tadbeesaChannelId
 * @returns {boolean}
 */
export function isTadbeesaLine(item, tadbeesaChannelId) {
  if (item?.channelId == null || tadbeesaChannelId == null) return false;

  return String(item.channelId) === String(tadbeesaChannelId);
}

/**
 * The most pieces ONE of her تدبيسة lines may ask for: what the line already
 * holds plus what is still on the shelf for that size.
 *
 * Not just `availableQty`. The backend's `resizeResaleLine()` claims only the
 * DIFFERENCE between the new quantity and the line's saved one, so the pieces
 * she already holds are hers and never counted against the shelf again —
 * capping a held line at the shelf count alone would block a legal raise
 * (holding 2 with 1 left must reach 3, not stop at 1). An unsaved line holds
 * nothing yet, so its cap is the shelf count.
 *
 * `availableQty` is the VARIANT's `resaleAvailableQty` (the size's own count,
 * via include=products.variants), never the post's total across every size.
 * Both inputs are floored at 0 so a stale or missing count can only narrow
 * the cap, never widen it — the backend still refuses anything past it.
 *
 * @param {number|null|undefined} savedQty - The line's saved quantity; 0/null for an unsaved line.
 * @param {number|null|undefined} availableQty - Pieces of this size still on the shelf.
 * @returns {number}
 */
export function resaleLineCap(savedQty, availableQty) {
  const held = Math.max(0, Number(savedQty) || 0);
  const left = Math.max(0, Number(availableQty) || 0);

  return held + left;
}

/** JSON:API error code of the backend's "fewer pieces than asked for" 409
 *  (`TadbeesaNotEnoughPiecesException::CODE`, BE→FE brief 2026-09-22). */
export const TADBEESA_NOT_ENOUGH_PIECES = 'tadbeesa.not_enough_pieces';

/**
 * Is this the backend refusing a تدبيسة quantity because the shelf is short?
 * Reads the `code` errorFromJsonApi lifts off `errors[0]`.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isTadbeesaRefusal(error) {
  return error?.code === TADBEESA_NOT_ENOUGH_PIECES;
}

/**
 * The pieces actually left, as the refusal reports them (`errors[0].meta.left`,
 * BE→FE brief 2026-09-23-tadbeesa-shelf-filter), or null when the response
 * carries no usable count — the caller then refetches the size instead.
 * Zero is a real answer ("the size is gone"), not an absence.
 *
 * @param {unknown} error
 * @returns {number|null}
 */
export function refusalPiecesLeft(error) {
  const left = error?.meta?.left;
  return Number.isInteger(left) ? Math.max(0, left) : null;
}

/** The club's shelf page (routes.js, tadbeesa P3-A). */
export const TADBEESA_PAGE_PATH = '/tadbeesa/';

/**
 * Where a tap on a post card's channel header goes: the تدبيسة shelf for a
 * resale post, nowhere for any other channel (a no-op, as it has always been).
 * `meta` is the second argument of PostCard's `channel-click`.
 *
 * @param {{ isResale?: boolean }|null|undefined} meta
 * @returns {string|null}
 */
export function channelTapTarget(meta) {
  return meta?.isResale === true ? TADBEESA_PAGE_PATH : null;
}
